import React from 'react';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';

import { ChipFilter } from '@companycam/slab-web';
import { translate } from 'translations/i18n';
import {
  getAddressComponent,
  GooglePlaceAutocomplete,
} from 'components/shared/GooglePlaceAutocomplete';
import { type GooglePlaceDetails } from 'data/v3/data-contracts';

import { type UseProfileFormReturn } from '../useProfileForm';
import { Row } from '../CommonComponents';

const ChipRow = styled.div`
  display: flex;
  flex: row;
  overflow: scroll;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const ChipContainer = styled.span`
  margin-right: var(--cc_size_spacing_xs);
`;

type ServicesProps = {
  formMethods: UseProfileFormReturn;
};

export const ServicesField = ({ formMethods }: ServicesProps) => {
  const handlePlaceSelect = (place: GooglePlaceDetails) => {
    const serviceArea = getAddressComponent(
      place.placeDetails.addressComponents,
      ['locality', 'administrative_area_level_1'],
      ', ',
    );

    const currentServiceAreas = formMethods.getValues('serviceAreas');
    const newServiceAreas = [serviceArea, ...currentServiceAreas];
    const dedupedServiceAreas = Array.from(new Set(newServiceAreas));
    formMethods.setValue('serviceAreas', dedupedServiceAreas);
    formMethods.trigger('serviceAreas');
  };

  const serviceAreas = formMethods.watch('serviceAreas');

  const onServiceAreaRemoval = (removedIndex: number) => {
    const newServiceAreas = serviceAreas.filter(
      (_, serviceAreaIndex) => serviceAreaIndex !== removedIndex,
    );
    formMethods.setValue('serviceAreas', newServiceAreas);
  };

  return (
    <Controller
      name="serviceAreas"
      control={formMethods.control}
      render={({ field, fieldState }) => (
        <>
          <Row>
            <GooglePlaceAutocomplete
              onPlaceSelect={handlePlaceSelect}
              label={translate('portfolio.profile.serviceAreas')}
              placeholder={translate(
                'portfolio.profile.serviceAreasPlaceholder',
              )}
              status={fieldState.error ? 'error' : 'normal'}
              message={fieldState.error?.message}
              primaryType="(cities)"
            />
          </Row>
          {serviceAreas.length ? (
            <ChipRow>
              {field.value.map((serviceArea, index) => (
                <ChipContainer key={serviceArea}>
                  <ChipFilter
                    closeAction={{
                      accessibilityLabel: translate('common.remove'),
                      onClick: () => onServiceAreaRemoval(index),
                    }}
                  >
                    {serviceArea}
                  </ChipFilter>
                </ChipContainer>
              ))}
            </ChipRow>
          ) : null}
        </>
      )}
    />
  );
};
